import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@primitives/form";
import { Button } from "@primitives/button";
import { Input } from "@primitives/input";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/server/apiError";
import {
  sendVerificationCode,
  verifyEmail,
} from "@/server/services/authService";
import { VerificationCodeSchema, VerificationCodeType } from "@/schemas/auth";

const RESEND_COOLDOWN_SECONDS = 60;

interface Props {
  email: string;
  onSuccess: () => void;
  onExpired: () => void;
  onBack: () => void;
  /**
   * EMAIL_VERIFICATION_ENABLED was flipped off mid-flow (NotEnabled from
   * verify or resend). Registration no longer requires a verified email,
   * so skip forward instead of showing an error.
   */
  onSkip: () => void;
  initialCooldown?: number;
}

export default function CodeVerificationStep({
  email,
  onSuccess,
  onExpired,
  onBack,
  onSkip,
  initialCooldown = RESEND_COOLDOWN_SECONDS,
}: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [cooldown, setCooldown] = useState(initialCooldown);
  const [isResending, setIsResending] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function startCooldown(from = RESEND_COOLDOWN_SECONDS) {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setCooldown(from);
    intervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(intervalRef.current!);
        }
        return next;
      });
    }, 1000);
  }

  useEffect(() => {
    if (initialCooldown > 0) {
      startCooldown(initialCooldown);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // initialCooldown is intentionally read once at mount — the countdown
    // should not restart when the prop changes after the component is displayed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm<VerificationCodeType>({
    resolver: zodResolver(VerificationCodeSchema),
    defaultValues: { code: "" },
  });

  async function onSubmit({ code }: VerificationCodeType) {
    try {
      await verifyEmail(email, code);
      onSuccess();
    } catch (error) {
      if (error instanceof ApiError) {
        const errorCode = error.data?.title;

        if (errorCode === "errors.EmailVerification.NotEnabled") {
          onSkip();
          return;
        }

        if (errorCode === "errors.EmailVerification.NotFound") {
          onExpired();
          return;
        }

        if (errorCode === "errors.EmailVerification.InvalidCode") {
          form.setError("code", {
            message: t("errors.EmailVerification.InvalidCode"),
          });
          return;
        }

        toast({
          // Coalesce before building the key array — i18next throws on an
          // undefined key entry rather than falling through to the fallback.
          description: t([errorCode ?? "errorOccurred", "errorOccurred"]),
          variant: "destructive",
        });
      } else {
        toast({
          description:
            error instanceof Error ? error.message : t("errorOccurred"),
          variant: "destructive",
        });
      }
    }
  }

  async function handleResend() {
    setIsResending(true);
    try {
      await sendVerificationCode(email);
      startCooldown();
      form.reset();
    } catch (error) {
      if (error instanceof ApiError) {
        const title = error.data?.title;

        if (title === "errors.EmailVerification.NotEnabled") {
          onSkip();
          return;
        }

        toast({
          // Array form: fall back to the generic message when the title has
          // no translation, instead of rendering the raw key.
          description: t([
            title ?? "errors.EmailVerification.CooldownActive",
            "errorOccurred",
          ]),
          variant: "destructive",
        });
      } else {
        toast({ description: t("errorOccurred"), variant: "destructive" });
      }
    } finally {
      setIsResending(false);
    }
  }

  return (
    <div className="my-7 space-y-4">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">
          {t("register.codeStep.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("register.codeStep.description", { email })}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="rounded-lg border-border bg-card py-6 text-center tracking-widest placeholder:opacity-70"
                    placeholder={t("register.codeStep.codePlaceholder")}
                    inputMode="numeric"
                    maxLength={6}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="h-12 w-full rounded-lg"
          >
            {form.formState.isSubmitting
              ? t("register.codeStep.verifying")
              : t("register.codeStep.verifyButton")}
          </Button>
        </form>
      </Form>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          className="text-muted-foreground underline"
          onClick={onBack}
        >
          {t("register.codeStep.backToEmail")}
        </button>

        <button
          type="button"
          disabled={cooldown > 0 || isResending}
          className="text-secondary underline disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleResend}
        >
          {cooldown > 0
            ? t("register.codeStep.resendIn", { seconds: cooldown })
            : t("register.codeStep.resendCode")}
        </button>
      </div>
    </div>
  );
}
